import { BackgroundCheckConfiguration } from './background-check'
import { DrugPanelType, DrugScreeningVendor } from './drug-screening'
import {
  DocumentProvider,
  LanguageContentObj,
  QualificationQuestionAnswerCreateParams,
  QualificationQuestionAnswerUpdateParams,
} from './qualification-question-answer'
import {
  WorkerApplicationItemStatus,
  WorkerApplicationExclusionType,
} from './worker-applications'
import { WorkerMediaEvaluationType } from './worker-media-evaluation'
import { CreateVettingCampaignRequest, VettingConfig } from './worker-vetting'

export enum ApplicationEntityType {
  SHIFT_REQUEST = 'SHIFT_REQUEST',
  ROLE = 'ROLE',
}

export enum ApplicationTemplateEntityType {
  ROLE = 'ROLE',
  ROLE_FAMILY = 'ROLE_FAMILY',
  TRABA_ACQUISITION_ROLE_FAMILY = 'TRABA_ACQUISITION_ROLE_FAMILY',
}

export type WorkerApplicationExclusions = WorkerApplicationExclusionType[]

export type ApplicationEntity = {
  id: string
  applicationEntityType: ApplicationEntityType
  shiftRequestId?: string
  roleId?: string
}

export enum ApplicationItemType {
  AI_VETTING_CALL = 'AI_VETTING_CALL',
  DRUG_SCREENING = 'DRUG_SCREENING',
  RESUME_UPLOAD = 'RESUME_UPLOAD',
  OPS_MANUAL_STEP = 'OPS_MANUAL_STEP',
  CUSTOM_QUESTION = 'CUSTOM_QUESTION',
  GHOST_PROFILE_LINKED = 'GHOST_PROFILE_LINKED', // When a Ghost profile becomes linked to an actual worker.
  VETTING_CAMPAIGN_SPREADSHEET = 'VETTING_CAMPAIGN_SPREADSHEET',
  STRIPE_ACCOUNT = 'STRIPE_ACCOUNT',
  STRIPE_W9 = 'STRIPE_W9',
  W2_APPROVAL = 'W2_APPROVAL',
  W2_ONBOARDING_PERSONAL_INFO = 'W2_ONBOARDING_PERSONAL_INFO',
  W2_ONBOARDING_TAXES = 'W2_ONBOARDING_TAXES',
  W2_ONBOARDING_WORK_AUTHORIZATION = 'W2_ONBOARDING_WORK_AUTHORIZATION',
  W2_ONBOARDING_BACKGROUND_CHECK = 'W2_ONBOARDING_BACKGROUND_CHECK',
  W2_ONBOARDING_PAPERWORK = 'W2_ONBOARDING_PAPERWORK',
  DOCUMENT_SIGNING = 'DOCUMENT_SIGNING',
  ONBOARDING_BACKGROUND_CHECK_1099 = 'ONBOARDING_BACKGROUND_CHECK_1099',
  ONBOARDING_INDEPENDENT_CONTRACTOR_AGREEMENT_1099 = 'ONBOARDING_INDEPENDENT_CONTRACTOR_AGREEMENT_1099',
  MEDIA_EVALUATION = 'MEDIA_EVALUATION',
  VIDEO_SUBMISSION = 'VIDEO_SUBMISSION',
}

export const APPLICATION_ITEM_TYPE_TO_TITLE: Record<
  ApplicationItemType,
  string
> = {
  [ApplicationItemType.AI_VETTING_CALL]: 'AI Vetting Call',
  [ApplicationItemType.DRUG_SCREENING]: 'Drug Screen',
  [ApplicationItemType.RESUME_UPLOAD]: 'Resume Upload',
  [ApplicationItemType.OPS_MANUAL_STEP]: 'Ops Manual Step',
  [ApplicationItemType.CUSTOM_QUESTION]: 'Screening Question',
  [ApplicationItemType.GHOST_PROFILE_LINKED]: 'Account Creation',
  [ApplicationItemType.VETTING_CAMPAIGN_SPREADSHEET]: 'Spreadsheet Entry',
  [ApplicationItemType.STRIPE_ACCOUNT]: 'Stripe Account',
  [ApplicationItemType.STRIPE_W9]: 'Stripe W9',
  [ApplicationItemType.W2_APPROVAL]: 'W2 Approval',
  [ApplicationItemType.W2_ONBOARDING_PERSONAL_INFO]: 'Personal Information',
  [ApplicationItemType.W2_ONBOARDING_TAXES]: 'Taxes',
  [ApplicationItemType.W2_ONBOARDING_WORK_AUTHORIZATION]: 'Work Authorization',
  [ApplicationItemType.W2_ONBOARDING_BACKGROUND_CHECK]: 'W2 Background Check',
  [ApplicationItemType.W2_ONBOARDING_PAPERWORK]: 'Legal Paperwork',
  [ApplicationItemType.DOCUMENT_SIGNING]: 'Document Signing',
  [ApplicationItemType.ONBOARDING_BACKGROUND_CHECK_1099]:
    '1099 Background Check',
  [ApplicationItemType.ONBOARDING_INDEPENDENT_CONTRACTOR_AGREEMENT_1099]:
    'Independent Contractor Agreement',
  // MEDIA_EVALUATION supports multiple media types (steel toed boots, licenses, etc.)
  [ApplicationItemType.MEDIA_EVALUATION]: 'Image Verification',
  [ApplicationItemType.VIDEO_SUBMISSION]: 'Video Submission',
}

export const APPLICATION_ITEM_TYPE_TO_SHORT_TITLE: Record<
  ApplicationItemType,
  string
> = {
  [ApplicationItemType.AI_VETTING_CALL]: 'AI Call',
  [ApplicationItemType.DRUG_SCREENING]: 'Drugs',
  [ApplicationItemType.RESUME_UPLOAD]: 'Resume',
  [ApplicationItemType.OPS_MANUAL_STEP]: 'Manual',
  [ApplicationItemType.CUSTOM_QUESTION]: 'Question',
  [ApplicationItemType.GHOST_PROFILE_LINKED]: 'Account',
  [ApplicationItemType.VETTING_CAMPAIGN_SPREADSHEET]: 'Spreadsheet',
  [ApplicationItemType.STRIPE_ACCOUNT]: 'Stripe',
  [ApplicationItemType.STRIPE_W9]: 'W9',
  [ApplicationItemType.W2_APPROVAL]: 'W2 Approval',
  [ApplicationItemType.W2_ONBOARDING_PERSONAL_INFO]: 'W2 Info',
  [ApplicationItemType.W2_ONBOARDING_TAXES]: 'Taxes',
  [ApplicationItemType.W2_ONBOARDING_WORK_AUTHORIZATION]: 'W2 Auth',
  [ApplicationItemType.W2_ONBOARDING_BACKGROUND_CHECK]: 'W2 BGC',
  [ApplicationItemType.W2_ONBOARDING_PAPERWORK]: 'Legal Paperwork',
  [ApplicationItemType.DOCUMENT_SIGNING]: 'Doc Sign',
  [ApplicationItemType.ONBOARDING_BACKGROUND_CHECK_1099]: '1099 BGC',
  [ApplicationItemType.ONBOARDING_INDEPENDENT_CONTRACTOR_AGREEMENT_1099]:
    '1099 ICA',
  // Currently MEDIA_EVALUATION *only* supports steel toed boots, so we add it in
  // the title for clarity until we *do* add other media collection types
  [ApplicationItemType.MEDIA_EVALUATION]: 'Steel Boots',
  [ApplicationItemType.VIDEO_SUBMISSION]: 'Video',
}

export const ENABLED_APPLICATION_ITEM_TYPES = [
  ApplicationItemType.AI_VETTING_CALL,
  ApplicationItemType.RESUME_UPLOAD,
  ApplicationItemType.CUSTOM_QUESTION,
  ApplicationItemType.OPS_MANUAL_STEP,
  ApplicationItemType.VETTING_CAMPAIGN_SPREADSHEET,
  ApplicationItemType.W2_ONBOARDING_BACKGROUND_CHECK,
  ApplicationItemType.ONBOARDING_BACKGROUND_CHECK_1099,
  ApplicationItemType.MEDIA_EVALUATION,
  ApplicationItemType.VIDEO_SUBMISSION,
  ApplicationItemType.DRUG_SCREENING,
  ApplicationItemType.DOCUMENT_SIGNING,
]

// Static application items that are editable by ops users for configuration purposes.
// "Static" refers to their ordering being uneditable, not necessarily their content.
// These items appear in the onboarding section and cannot be reordered but can be edited/configured.
export const EDITABLE_STATIC_ITEMS = [
  ApplicationItemType.W2_ONBOARDING_BACKGROUND_CHECK,
  ApplicationItemType.ONBOARDING_BACKGROUND_CHECK_1099,
]

// we will not allow ops to change the status of these application items
export const APPLICATION_ITEM_TYPES_THAT_CANNOT_BE_ACTIONED_ON = [
  ApplicationItemType.GHOST_PROFILE_LINKED,
  ApplicationItemType.STRIPE_ACCOUNT,
  ApplicationItemType.STRIPE_W9,
  ApplicationItemType.W2_APPROVAL,
  ApplicationItemType.W2_ONBOARDING_PERSONAL_INFO,
  ApplicationItemType.W2_ONBOARDING_TAXES,
  ApplicationItemType.W2_ONBOARDING_WORK_AUTHORIZATION,
  ApplicationItemType.W2_ONBOARDING_PAPERWORK,
  ApplicationItemType.ONBOARDING_INDEPENDENT_CONTRACTOR_AGREEMENT_1099,
]

// application item types that can be created but cannot be edited or deleted once created.
export const APPLICATION_ITEM_TYPES_THAT_CANNOT_BE_EDITED_OR_DELETED = [
  ApplicationItemType.DRUG_SCREENING,
]

// we will not allow ops to change the status of cancelled items (items that are tied to archived application items)
export const APPLICATION_STATUSES_THAT_CANNOT_BE_ACTIONED_ON = [
  WorkerApplicationItemStatus.PENDING_PREREQUISITES,
  WorkerApplicationItemStatus.CANCELED,
]

// these application items can be used multiple times in one the application
export const ALLOW_MULTIPLE_STEPS_OF_APPLICATION_ITEM_TYPES = [
  ApplicationItemType.OPS_MANUAL_STEP,
  ApplicationItemType.CUSTOM_QUESTION,
  ApplicationItemType.VIDEO_SUBMISSION,
  ApplicationItemType.MEDIA_EVALUATION,
  ApplicationItemType.DOCUMENT_SIGNING,
]

export enum ApplicationRecordStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DISABLED = 'DISABLED',
}

export enum ApplicationItemRecordStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export type ApplicationItem = {
  id: string
  applicationItemType: ApplicationItemType
  sourceId?: string
  applicationId: string
  recordStatus: ApplicationItemRecordStatus
  sectionIndex: ApplicationSectionIndex
  sectionItemIndex: number
  sectionDisplayOrderIndex?: number
  evalType?: WorkerMediaEvaluationType
  isInherited?: boolean
}

export enum ApplicationSectionIndex {
  SHIFT_APPLICATION = 0,
  ONBOARDING = 1,
}

export type ApplicationSection = {
  sectionIndex: ApplicationSectionIndex
  applicationItems: ApplicationItem[]
}

export type Application = {
  id: string
  recordStatus: ApplicationRecordStatus
  applicationEntities: ApplicationEntity[]
  applicationSections: ApplicationSection[]
  // TODO(ENG-14681): remove applicationItems once all clients use sections
  /** @deprecated This field has been deprecated; access items through sections and the helpers in application.utils.ts  */
  applicationItems: ApplicationItem[]
}

export type EmptyItemContent = object

export type ShiftInformationForVettingConfigApplicationItem = {
  shiftRoleId: string
  shiftRequestParentTitle?: string
  shiftStartTime?: string
  shiftRoleName: string
}

export type ApplicationItemAICallItemContent = {
  vettingConfig: VettingConfig
  shiftInformationForVettingConfig: ShiftInformationForVettingConfigApplicationItem
}

export type ApplicationItemAICallRequest = {
  applicationItemType: ApplicationItemType.AI_VETTING_CALL
  itemContent: ApplicationItemAICallItemContent
}

export type ApplicationItemAIResumeUploadRequest = {
  applicationItemType: ApplicationItemType.RESUME_UPLOAD
  itemContent: EmptyItemContent
}

export type ApplicationItemCustomQuestionItemContent = {
  createQualifierQuestionRequestDto?: QualificationQuestionAnswerCreateParams
  updateQualifierQuestionRequestDto?: QualificationQuestionAnswerUpdateParams
}

export type ApplicationItemCustomQuestionRequest = {
  applicationItemType: ApplicationItemType.CUSTOM_QUESTION
  itemContent: ApplicationItemCustomQuestionItemContent
}

export type OpsManualStepCreateParams = {
  workerAppTitle: LanguageContentObj[]
  workerAppDescription?: LanguageContentObj[]
  workerAppCtaText?: LanguageContentObj[]
  opsConsoleTitle: string
  opsConsoleDescription: string
}

export type OpsManualStepUpdateParams = {
  workerAppTitle?: LanguageContentObj[]
  workerAppDescription?: LanguageContentObj[]
  workerAppCtaText?: LanguageContentObj[]
  opsConsoleTitle?: string
  opsConsoleDescription?: string
  opsInputNotes?: string
  applicationItemId?: string
}

export type ApplicationItemBackgroundCheckItemContent = {
  backgroundCheckConfigurationDto?: BackgroundCheckConfiguration
}

export type ApplicationItemBackgroundCheckRequest = {
  applicationItemType: ApplicationItemType.ONBOARDING_BACKGROUND_CHECK_1099
  itemContent: ApplicationItemBackgroundCheckItemContent
}

export type ApplicationItemBackgroundCheckW2Request = {
  applicationItemType: ApplicationItemType.W2_ONBOARDING_BACKGROUND_CHECK
  itemContent: ApplicationItemBackgroundCheckItemContent
}

export type ApplicationItemOpsManualStepItemContent = {
  createOpsManualActionRequestDto?: OpsManualStepCreateParams
  updateOpsManualActionRequestDto?: OpsManualStepUpdateParams
}

export type ApplicationItemOpsManualStepRequest = {
  applicationItemType: ApplicationItemType.OPS_MANUAL_STEP
  itemContent: ApplicationItemOpsManualStepItemContent
}

export type ApplicationItemVettingCampaignSpreadsheetRequest = {
  applicationItemType: ApplicationItemType.VETTING_CAMPAIGN_SPREADSHEET
  itemContent: ApplicationItemVettingCampaignSpreadsheetItemContent
}

export type ApplicationItemW2ApprovalRequest = {
  applicationItemType: ApplicationItemType.W2_APPROVAL
  itemContent: EmptyItemContent
}

export type ApplicationItemVettingCampaignSpreadsheetItemContent = {
  vettingCampaign: CreateVettingCampaignRequest
}

type WorkerMediaEvaluationConfigurationDto = {
  type: WorkerMediaEvaluationType
}

export type ApplicationItemWorkerMediaEvaluationItemContent = {
  workerMediaEvaluationConfiguration: WorkerMediaEvaluationConfigurationDto
}

export type ApplicationItemWorkerMediaEvaluationRequest = {
  applicationItemType: ApplicationItemType.MEDIA_EVALUATION
  itemContent: ApplicationItemWorkerMediaEvaluationItemContent
}

export type ApplicationItemVideoSubmissionItemContent = {
  qualifierQuestion?: {
    questionTitle: LanguageContentObj[]
    applicationItemId?: string
    questionDescription?: LanguageContentObj[]
    answerValidityDays?: number
  }
  qualifierQuestionId?: string
}

export type ApplicationItemVideoSubmissionRequest = {
  applicationItemType: ApplicationItemType.VIDEO_SUBMISSION
  itemContent: ApplicationItemVideoSubmissionItemContent
}

export type ApplicationItemDrugScreeningItemContent = {
  drugScreeningVendor: DrugScreeningVendor
  drugPanelType: DrugPanelType
  autoRejectOnPositive: boolean
  autoCreateOrder: boolean
  deadlineDays: number
}

export type ApplicationItemDrugScreeningRequest = {
  applicationItemType: ApplicationItemType.DRUG_SCREENING
  itemContent: ApplicationItemDrugScreeningItemContent
}

export type ApplicationItemDocumentSigningItemContent = {
  // provider controls which signing flow is used. Optional for backwards
  // compatibility with existing DOCUSIGN-only configs; defaults to DOCUSIGN
  // server-side when omitted.
  provider?: DocumentProvider
  // Required when provider=DOCUSIGN, null/unset when provider=INHOUSE.
  docusignTemplateId?: string
  // Required when provider=INHOUSE, null/unset when provider=DOCUSIGN.
  // References a DocumentTemplate with provider=INHOUSE + storageKey.
  documentTemplateId?: string
  returnUrl?: string
  title?: string
  description?: string
}

export type ApplicationItemDocumentSigningRequest = {
  applicationItemType: ApplicationItemType.DOCUMENT_SIGNING
  itemContent: ApplicationItemDocumentSigningItemContent
}

export type ApplicationItemRequestType =
  | ApplicationItemAICallRequest
  | ApplicationItemAIResumeUploadRequest
  | ApplicationItemCustomQuestionRequest
  | ApplicationItemOpsManualStepRequest
  | ApplicationItemVettingCampaignSpreadsheetRequest
  | ApplicationItemW2ApprovalRequest
  | ApplicationItemBackgroundCheckRequest
  | ApplicationItemBackgroundCheckW2Request
  | ApplicationItemWorkerMediaEvaluationRequest
  | ApplicationItemVideoSubmissionRequest
  | ApplicationItemDrugScreeningRequest
  | ApplicationItemDocumentSigningRequest

export type ApplicationItemContent =
  | EmptyItemContent
  | ApplicationItemAICallItemContent
  | ApplicationItemCustomQuestionItemContent
  | ApplicationItemOpsManualStepItemContent
  | ApplicationItemVettingCampaignSpreadsheetItemContent
  | ApplicationItemBackgroundCheckItemContent
  | ApplicationItemWorkerMediaEvaluationItemContent
  | ApplicationItemVideoSubmissionItemContent
  | ApplicationItemDrugScreeningItemContent
  | ApplicationItemDocumentSigningItemContent

// TODO(gail...): revisit separation of create & update types for saving app items
export type UpsertApplicationItemRequestType = ApplicationItemRequestType & {
  applicationItemId?: string
  applicationItemSourceId?: string
  itemContent: ApplicationItemContent
  sectionDisplayOrderIndex?: number
  workerApplicationExclusions?: WorkerApplicationExclusions
  isInherited?: boolean
}

export type ApplicationForShiftRequestRequest = {
  applicationItems: ApplicationItemRequestType[]
  sourceApplicationId?: string
  templateEntityType?: ApplicationTemplateEntityType
}

export type AdditionalInformationForItemCreateOrUpdate = {
  shiftInformationForVettingConfig?: ShiftInformationForVettingConfigApplicationItem
}
